/**
 * PATH: apps/quote/src/components/builder/sheets/CustomColumnsSection.tsx
 * FILE_ID: QB-SETUP-CUSTOMCOLS-001
 * PURPOSE: Setup-tab section for declaring per-quote custom columns + their
 *          default values (the cascade head — line.custom_values overrides this).
 * WHY: Estimators need to add ad-hoc columns (e.g. "Fume Extraction", "Crew
 *      Code", "Site Multiplier") without a code change. Setup defaults apply
 *      to every existing + future line; per-line overrides win when set.
 * FLOW:
 *   ┌────────────────┐     ┌──────────────────────┐     ┌─────────────────┐
 *   │ Add column     │ --> │ quote.config.custom_ │ --> │ Grid renders    │
 *   │ (key, label,   │     │ columns array        │     │ Custom band;    │
 *   │  type, default)│     │ (PATCH /quotes/<id>) │     │ each row reads  │
 *   └────────────────┘     └──────────────────────┘     │ override > def. │
 *                                                       └─────────────────┘
 * DEPENDENCIES:
 *   - quoteApi.updateQuote — persists `quote.config.custom_columns`
 *   - QB-SCOPESHEET-COLUMNS-001 — buildCustomColumnBand consumes this list
 * RELATED:
 *   - apps/quote/src/components/builder/scope-sheet-grid/ScopeSheetGridTypes.ts
 *     (CustomColumnDef definition)
 */

import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Plus, Trash2, GripVertical } from 'lucide-react'
import { cn } from '@/lib/utils'
import { quoteApi, type QuoteDetail } from '@/lib/quoteApi'
import type { CustomColumnDef } from '../scope-sheet-grid/ScopeSheetGridTypes'

interface CustomColumnsSectionProps {
  quoteId: string
  quote: QuoteDetail
  isEditable: boolean
  defaultExpanded?: boolean
}

function readCustomColumns(quote: QuoteDetail): CustomColumnDef[] {
  const cfg = (quote.config ?? {}) as Record<string, unknown>
  const raw = cfg.custom_columns
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (x): x is CustomColumnDef =>
      typeof x === 'object' && x !== null && typeof (x as CustomColumnDef).key === 'string',
  )
}

function slugifyKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
}

export default function CustomColumnsSection({
  quoteId,
  quote,
  isEditable,
  defaultExpanded = false,
}: CustomColumnsSectionProps) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const columns = useMemo(() => readCustomColumns(quote), [quote])

  const persist = useMutation({
    mutationFn: async (next: CustomColumnDef[]) => {
      const cfg = (quote.config ?? {}) as Record<string, unknown>
      return quoteApi.updateQuote(quoteId, {
        config: { ...cfg, custom_columns: next },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', 'builder', quoteId] })
    },
  })

  const handleAdd = () => {
    if (!isEditable) return
    const existingKeys = new Set(columns.map((c) => c.key))
    let i = columns.length + 1
    let key = `custom_${i}`
    while (existingKeys.has(key)) {
      i += 1
      key = `custom_${i}`
    }
    const next: CustomColumnDef[] = [
      ...columns,
      {
        key,
        label: `Custom ${i}`,
        type: 'text',
        width: 90,
        default_value: null,
        sort_order: (columns[columns.length - 1]?.sort_order ?? 0) + 10,
      },
    ]
    persist.mutate(next)
  }

  const handleUpdate = (idx: number, patch: Partial<CustomColumnDef>) => {
    if (!isEditable) return
    const next = columns.map((c, i) => (i === idx ? { ...c, ...patch } : c))
    persist.mutate(next)
  }

  const handleRemove = (idx: number) => {
    if (!isEditable) return
    const next = columns.filter((_, i) => i !== idx)
    persist.mutate(next)
  }

  const handleRelabel = (idx: number, label: string) => {
    if (!isEditable) return
    const current = columns[idx]
    if (!current) return
    // If the user still has the auto-generated key, keep it in sync with the
    // label as a quality-of-life thing. Once they manually edit the key, we
    // stop syncing (key becomes the stable identifier).
    const keyShouldFollow =
      current.key.startsWith('custom_') ||
      current.key === slugifyKey(current.label || '')
    const newKey = keyShouldFollow ? slugifyKey(label) || current.key : current.key
    handleUpdate(idx, { label, key: newKey })
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="font-semibold">Custom Columns</span>
          <span className="text-xs opacity-70">
            • Per-line overrides cascade through these Setup defaults
          </span>
        </div>
        <span className="text-xs opacity-50">
          {columns.length === 0 ? 'none' : `${columns.length} column${columns.length === 1 ? '' : 's'}`}
        </span>
      </button>

      {expanded && (
        <div className="bg-white dark:bg-slate-900 p-4 space-y-3">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Add ad-hoc columns to the scope grid (e.g. "Fume Extraction",
            "Crew Code"). Each line inherits the default value below; override
            it directly in the cell when reality differs.
          </p>

          {columns.length === 0 && (
            <div className="text-sm text-slate-400 dark:text-slate-500 italic py-3 text-center border border-dashed rounded">
              No custom columns yet. Click "Add column" below.
            </div>
          )}

          {columns.length > 0 && (
            <div className="grid grid-cols-[24px_1fr_140px_110px_140px_36px] items-center gap-2 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              <div />
              <div>Label</div>
              <div>Key</div>
              <div>Type</div>
              <div>Default</div>
              <div />
            </div>
          )}

          {columns.map((col, idx) => (
            <div
              key={col.key}
              className="grid grid-cols-[24px_1fr_140px_110px_140px_36px] items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 dark:hover:bg-slate-800/50"
            >
              <div className="text-slate-300">
                <GripVertical className="w-4 h-4" />
              </div>
              <input
                type="text"
                value={col.label}
                disabled={!isEditable}
                onChange={(e) => handleRelabel(idx, e.target.value)}
                placeholder="Column label"
                className="px-2 py-1 text-sm border rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-rose-400 disabled:opacity-50"
              />
              <input
                type="text"
                value={col.key}
                disabled={!isEditable}
                onChange={(e) =>
                  handleUpdate(idx, { key: slugifyKey(e.target.value) || col.key })
                }
                placeholder="key_name"
                className="px-2 py-1 text-xs font-mono border rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-rose-400 disabled:opacity-50"
              />
              <select
                value={col.type ?? 'text'}
                disabled={!isEditable}
                onChange={(e) =>
                  handleUpdate(idx, {
                    type: e.target.value as 'text' | 'number' | 'currency',
                  })
                }
                className="px-2 py-1 text-xs border rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-rose-400 disabled:opacity-50"
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="currency">Currency</option>
              </select>
              <input
                type={col.type === 'number' || col.type === 'currency' ? 'number' : 'text'}
                value={col.default_value === null || col.default_value === undefined ? '' : String(col.default_value)}
                disabled={!isEditable}
                onChange={(e) => {
                  const raw = e.target.value
                  let v: string | number | null = raw
                  if (raw === '') v = null
                  else if (col.type === 'number' || col.type === 'currency') {
                    const n = Number.parseFloat(raw)
                    v = Number.isFinite(n) ? n : null
                  }
                  handleUpdate(idx, { default_value: v })
                }}
                placeholder="default value"
                className="px-2 py-1 text-sm border rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-rose-400 disabled:opacity-50"
              />
              <button
                onClick={() => handleRemove(idx)}
                disabled={!isEditable}
                title="Remove column"
                className={cn(
                  'flex items-center justify-center w-7 h-7 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors',
                  !isEditable && 'opacity-30 cursor-not-allowed',
                )}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button
            onClick={handleAdd}
            disabled={!isEditable || persist.isPending}
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded',
              isEditable
                ? 'bg-rose-600 text-white hover:bg-rose-700'
                : 'bg-slate-200 text-slate-500 cursor-not-allowed',
            )}
          >
            <Plus className="w-4 h-4" />
            Add column
          </button>

          {persist.isError && (
            <div className="text-xs text-red-600 dark:text-red-400">
              Failed to save: {(persist.error as Error)?.message ?? 'unknown error'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
